import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';

const FAQSection = () => {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (index) => {
    setOpenItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const faqs = [
    {
      question: "How long does legal gold export take with GOLDCIF?",
      answer: "GOLDCIF processes legal gold exports within 5 working days maximum. This includes verification, testing, documentation, and customs clearance. We handle all paperwork to ensure compliance with international regulations.",
      schema: {
        "@type": "Question",
        "name": "How long does legal gold export take with GOLDCIF?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "GOLDCIF processes legal gold exports within 5 working days maximum. This includes verification, testing, documentation, and customs clearance."
        }
      }
    },
    {
      question: "Do you cover export taxes and fees?",
      answer: "Yes, GOLDCIF covers all local fees up to the final destination. We handle export taxes, customs duties, documentation fees, and all regulatory compliance costs. You receive your payment after all fees are deducted transparently.",
      schema: {
        "@type": "Question", 
        "name": "Do you cover export taxes and fees?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, GOLDCIF covers all local fees up to the final destination including export taxes, customs duties, documentation fees, and regulatory compliance costs."
        }
      }
    },
    {
      question: "What countries do you operate in?",
      answer: "GOLDCIF operates primarily in Uganda and Dubai. We work with licensed artisanal gold producers across Africa, with our main operations centers strategically located to serve the African mining community and international markets.",
      schema: {
        "@type": "Question",
        "name": "What countries do you operate in?", 
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "GOLDCIF operates primarily in Uganda and Dubai, serving licensed artisanal gold producers across Africa."
        }
      }
    },
    {
      question: "Who can sell gold to GOLDCIF?",
      answer: "We work exclusively with licensed artisanal gold producers and official mandates with proper documentation. You must have valid mining/export licenses, recent assay reports, and be able to provide collateral for export costs. We do not work with unverified brokers or unlicensed operators.",
      schema: {
        "@type": "Question",
        "name": "Who can sell gold to GOLDCIF?",
        "acceptedAnswer": {
          "@type": "Answer", 
          "text": "GOLDCIF works exclusively with licensed artisanal gold producers and official mandates with proper documentation, valid licenses, and recent assay reports."
        }
      }
    },
    {
      question: "What is the minimum gold purity required?",
      answer: "While we accept various purity levels, we recommend purifying your gold to at least 96% for better terms. All gold must be tested and verified by our local lawyer and agent team before any transaction proceeds.",
      schema: {
        "@type": "Question",
        "name": "What is the minimum gold purity required?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "GOLDCIF recommends gold purity of at least 96% for optimal terms. All gold must be tested and verified before transactions."
        }
      }
    },
    {
      question: "How does the verification process work?", 
      answer: "Our local lawyer and agent team validates your gold and licenses through a comprehensive process. This includes document verification, physical gold testing, license validation, and compliance checks. The entire verification is completed as part of our 5-day timeline.",
      schema: {
        "@type": "Question",
        "name": "How does the verification process work?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our local lawyer and agent team validates gold and licenses through comprehensive document verification, physical testing, and compliance checks within our 5-day timeline."
        }
      }
    },
    {
      question: "What makes GOLDCIF different from other gold buyers?",
      answer: "GOLDCIF is the final buyer, not a middleman. We provide complete legal compliance, cover all taxes and fees, handle full logistics, and ensure transparent processes. Our direct buyer approach means single trusted contact from export to final delivery, with operations in Uganda and Dubai.",
      schema: {
        "@type": "Question", 
        "name": "What makes GOLDCIF different from other gold buyers?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "GOLDCIF is the final buyer providing complete legal compliance, tax coverage, full logistics, and transparent processes with operations in Uganda and Dubai."
        }
      }
    },
    {
      question: "Is collateral required for export costs?",
      answer: "Yes, collateral may be required to cover export costs. This ensures all fees, taxes, and logistics expenses are covered during the export process. The collateral requirement is discussed during the application process and varies based on transaction size and client history.",
      schema: {
        "@type": "Question",
        "name": "Is collateral required for export costs?", 
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, collateral may be required to cover export costs including fees, taxes, and logistics expenses. Requirements are discussed during application."
        }
      }
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => faq.schema)
  };

  return (
    <section id="faq" className="py-24 bg-[#040F2D]">
      {/* Schema.org JSON-LD for FAQ */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-6">
            <HelpCircle className="h-10 w-10 text-[#D4AF37] mr-4" />
            <h2 className="text-4xl font-bold text-white">Frequently Asked Questions</h2>
          </div>
          <h3 className="text-xl text-white/70 max-w-3xl mx-auto">
            Everything you need to know about legal gold export with GOLDCIF
          </h3>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <Card key={index} className="border border-white/10 hover:border-[#D4AF37]/50 transition-all duration-300">
              <CardContent className="p-0">
                <button
                  onClick={() => toggleItem(index)}
                  className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <h4 className="text-lg font-semibold text-white pr-4">
                    {faq.question}
                  </h4>
                  {openItems[index] ? (
                    <ChevronUp className="h-5 w-5 text-[#D4AF37] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-[#D4AF37] flex-shrink-0" />
                  )}
                </button>
                
                {openItems[index] && (
                  <div className="px-6 pb-6">
                    <div className="border-t border-white/10 pt-4">
                      <p className="text-white/80 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-[#040F2D]/5 to-[#D4AF37]/5 rounded-xl p-8 border border-[#D4AF37]/20">
            <h4 className="text-2xl font-bold text-white mb-4">More Questions?</h4>
            <p className="text-white/80 mb-6">
              Our team is ready to help you understand the legal gold export process.
            </p>
            <button
              onClick={() => document.getElementById('application').scrollIntoView({ behavior: 'smooth' })}
              className="bg-[#D4AF37] hover:bg-[#C5A059] text-white px-8 py-3 rounded-lg font-semibold transition-all transform hover:scale-105"
            >
              Contact Our Team
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;